import type { ChangeEvent, InputHTMLAttributes, ReactNode, Ref } from 'react';

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'className' | 'onChange' | 'size'
> {
  label?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  invalid?: boolean;
  className?: string;
  ref?: Ref<HTMLInputElement>;
  onCheckedChange?: (checked: boolean) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

/** Binary on/off toggle. Keeps a native checkbox as the stateful core
 *  (for Tab order, form submit, keyboard Space) with role="switch"
 *  promoting the ARIA semantics. Visual track/thumb is fully ours. */
export function Switch(props: SwitchProps): ReactNode {
  const {
    label,
    size = 'md',
    invalid = false,
    className,
    ref,
    onCheckedChange,
    onChange,
    checked,
    disabled,
    ...rest
  } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e);
    onCheckedChange?.(e.target.checked);
  };

  const stateAttr =
    disabled === true ? 'disabled' : invalid ? 'invalid' : checked === true ? 'on' : 'off';

  const classes = ['vq-switch', className].filter(Boolean).join(' ');

  return (
    <label className={classes} data-component="Switch" data-size={size} data-state={stateAttr}>
      <input
        {...rest}
        ref={ref}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-checked={checked === true}
        onChange={handleChange}
      />
      <span className="vq-switch__track" aria-hidden="true">
        <span className="vq-switch__thumb" />
      </span>
      {label !== undefined ? <span className="vq-switch__label">{label}</span> : null}
    </label>
  );
}
